import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Baby, Loader2, Plus } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { NewPregnancyModal } from '../components/NewPregnancyModal';
import type { Pregnancy, PREGNANCY_STATUS_COLORS, PREGNANCY_STATUS_LABELS } from '../types/pregnancy';

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  delivered: 'bg-blue-100 text-blue-800',
  miscarriage: 'bg-gray-100 text-gray-800',
  terminated: 'bg-gray-100 text-gray-800',
};

const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  delivered: 'Delivered',
  miscarriage: 'Miscarriage',
  terminated: 'Terminated',
};

function gestationToday(lmp: string): string {
  if (!lmp) return '';
  const diff = Date.now() - new Date(lmp).getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days < 0) return '';
  const weeks = Math.floor(days / 7);
  const d = days % 7;
  return `${weeks}w ${d}d`;
}

export function PregnancyListPage() {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const { get, post } = useApi();
  const { userRole } = useAuth();
  const isDoctor = userRole === 'doctor';

  const [pregnancies, setPregnancies] = useState<Pregnancy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);
  const [patientName, setPatientName] = useState('');

  const fetchPregnancies = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const [pregRes, patRes] = await Promise.all([
        get<Pregnancy[]>(`/patients/${patientId}/pregnancies`),
        get<{ firstName: string; lastName: string }>(`/patients/${patientId}`),
      ]);
      if (pregRes.success && pregRes.data) setPregnancies(pregRes.data);
      else setError(pregRes.error || 'Failed to load pregnancies');
      if (patRes.success && patRes.data) {
        setPatientName(`${patRes.data.firstName || ''} ${patRes.data.lastName || ''}`.trim());
      }
    } catch {
      setError('Failed to load pregnancies');
    } finally {
      setLoading(false);
    }
  }, [patientId, get]);

  useEffect(() => { fetchPregnancies(); }, [fetchPregnancies]);

  const handleCreatePregnancy = useCallback(async (data: Record<string, unknown>) => {
    if (!patientId) return;
    const res = await post<Pregnancy>(`/patients/${patientId}/pregnancies`, data);
    if (res.success && res.data) {
      setPregnancies(prev => [res.data!, ...prev]);
      setShowNewModal(false);
    } else {
      throw new Error(res.error || 'Failed to create pregnancy');
    }
  }, [patientId, post]);

  return (
    <main className="max-w-5xl mx-auto px-4 py-6">
      <nav className="text-xs text-gray-500 mb-2 flex items-center gap-1">
        <button onClick={() => navigate('/dashboard')} className="hover:text-blue-600">Dashboard</button>
        <span>&rsaquo;</span>
        <button onClick={() => navigate('/patients')} className="hover:text-blue-600">Patients</button>
        <span>&rsaquo;</span>
        {patientName && (
          <>
            <button onClick={() => navigate(`/patients/${patientId}`)} className="hover:text-blue-600">{patientName}</button>
            <span>&rsaquo;</span>
          </>
        )}
        <span className="text-gray-700">Pregnancies</span>
      </nav>

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/clinical/${patientId}`)} className="p-1 text-gray-400 hover:text-gray-600">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-xl font-bold text-gray-900">Pregnancies</h1>
          {patientName && <span className="text-sm text-gray-500">— {patientName}</span>}
        </div>
        {isDoctor && (
          <button onClick={() => setShowNewModal(true)} className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-md hover:bg-teal-700 flex items-center gap-2">
            <Plus className="h-4 w-4" /> New Pregnancy
          </button>
        )}
      </div>

      {error && <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-teal-600" /></div>
      ) : pregnancies.length === 0 ? (
        <div className="text-center py-12">
          <Baby className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">No pregnancies recorded for this patient.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {pregnancies.map(p => (
            <button
              key={p.pregnancyId}
              onClick={() => navigate(`/patients/${patientId}/pregnancies/${p.pregnancyId}`, { state: { pregnancy: p, patientName } })}
              className="w-full bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3 text-left hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-gray-900">EDD: {p.edd}</span>
                  {p.status === 'active' && p.lmp && (
                    <span className="ml-3 text-xs text-gray-500">{gestationToday(p.lmp)} gestation</span>
                  )}
                </div>
                <span className={`px-2 py-0.5 text-xs rounded-full ${STATUS_COLORS[p.status] || 'bg-gray-100 text-gray-800'}`}>
                  {STATUS_LABELS[p.status] || p.status}
                </span>
              </div>
              <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                <span>G{p.gravidity}P{p.parity}</span>
                <span>Est. by {p.gestationEstablishedBy}</span>
                {p.rhesusStatus && <span>Rh {p.rhesusStatus === 'positive' ? '+' : '−'}</span>}
                {p.visitCount !== undefined && <span>{p.visitCount} visit{p.visitCount !== 1 ? 's' : ''}</span>}
              </div>
            </button>
          ))}
        </div>
      )}

      {showNewModal && (
        <NewPregnancyModal onSave={handleCreatePregnancy} onCancel={() => setShowNewModal(false)} />
      )}
    </main>
  );
}
